/**
 * A show's flyer as the upload route takes it, the way the iOS app prepares one: the picture shrunk to a
 * JPEG under the server's 2 MB, longest side 2000 px at most, transparency flattened onto white.
 */

/** The server refuses a flyer over 2 MB decoded; stay a little under it. */
export const FLYER_MAX_BYTES = 1_900_000;
const LONGEST_SIDE = 2000;
const QUALITIES = [0.85, 0.7, 0.55, 0.4, 0.3];

export const FLYER_UNREADABLE = "Couldn't read that image. Pick a PNG, JPEG, WEBP or GIF.";
export const FLYER_TOO_LARGE = "That image is still over 2 MB after shrinking it. Pick a smaller one.";
export const FLYER_UPLOAD_FAILED_ON_THE_WAY = "Something went wrong while sending the flyer. Try again.";
export const FLYER_REMOVE_FAILED_ON_THE_WAY = "Something went wrong while removing the flyer. Try again.";

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(FLYER_UNREADABLE));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });
}

/** The picked file as a JPEG data URL under `FLYER_MAX_BYTES`. Throws the sentence to show. */
export async function flyerDataUrl(file: File): Promise<string> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(FLYER_UNREADABLE);
  }
  try {
    let side = LONGEST_SIDE;
    for (let attempt = 0; attempt < 4; attempt++) {
      const scale = Math.min(1, side / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const g = canvas.getContext("2d");
      if (!g) throw new Error(FLYER_UNREADABLE);
      g.fillStyle = "#ffffff";
      g.fillRect(0, 0, canvas.width, canvas.height);
      g.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      for (const quality of QUALITIES) {
        const blob = await toBlob(canvas, quality);
        if (blob && blob.size <= FLYER_MAX_BYTES) return blobToDataUrl(blob);
      }
      side *= 0.75;
    }
  } finally {
    bitmap.close();
  }
  throw new Error(FLYER_TOO_LARGE);
}
